import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  Spinner,
  apiFetch,
  type ActionLinkContext,
} from "@dearbill/shared";
import { PortalShell } from "./PortalShell";
import { ActionLinkSign } from "./actionlink/ActionLinkSign";
import { ActionLinkUpload } from "./actionlink/ActionLinkUpload";
import { claimActionToken, forgetActionToken } from "./actionlink/actionToken";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const EXPIRED_MESSAGE =
  "This link has expired. Ask us to send a fresh one — we can do it while you're on the phone.";
const LOAD_FAILED_MESSAGE = "We couldn't open this link. Check your connection and try again.";

/** What blocks the page: a dead link (terminal) or a failed load (retryable). */
type PageError = "expired" | "load-failed";

/**
 * The `/do` mid-call action page. No portal session: the action-link token is
 * the only authorization. It arrives in the `#action=` fragment, is stripped
 * from the address bar at mount (history and bookmarks must not keep it) and
 * is kept in this tab's `sessionStorage` (`actionlink/actionToken`) so a
 * reload still works. It is replayed only as `Authorization: Bearer` to the
 * `/api/links/*` routes.
 *
 * `GET /api/links/context` decides what renders: the sign or consent signing
 * flow (`ActionLinkSign`) or the camera-first upload loop
 * (`ActionLinkUpload`). Every token failure is the server's one opaque 401,
 * shown as the ask-for-a-fresh-link message, which also forgets the stored
 * token; any other failure offers a retry. A completed signature forgets the
 * token too.
 */
export function ActionLinkPage(): ReactNode {
  // Claimed during the first render so nothing flashes before the effect runs.
  const [token] = useState(claimActionToken);
  const [context, setContext] = useState<ActionLinkContext | null>(null);
  const [error, setError] = useState<PageError | null>(token === null ? "expired" : null);
  // StrictMode runs effects twice; the strip-and-fetch must fire once.
  const claimed = useRef(false);

  const expire = useCallback((): void => {
    forgetActionToken();
    setError("expired");
  }, []);

  const loadContext = useCallback((): void => {
    if (token === null) return;
    setError(null);
    apiFetch<ActionLinkContext>("/api/links/context", {}, { baseUrl: API_BASE, token })
      .then(setContext)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          expire();
          return;
        }
        setError("load-failed");
      });
  }, [token, expire]);

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    // Strip the token from the address bar first, keeping it out of history
    // and bookmarks; the router's own history state is preserved.
    const routerState: unknown = window.history.state;
    window.history.replaceState(routerState, "", window.location.pathname + window.location.search);
    loadContext();
  }, [loadContext]);

  let content: ReactNode;
  if (error === "expired") {
    content = <Alert tone="warning" title={EXPIRED_MESSAGE} />;
  } else if (error === "load-failed") {
    content = (
      <div className="flex flex-col gap-4">
        <Alert tone="warning" title={LOAD_FAILED_MESSAGE} />
        <Button variant="secondary" size="md" className="self-start" onClick={loadContext}>
          Try again
        </Button>
      </div>
    );
  } else if (context === null || token === null) {
    content = (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Opening your link…</span>
      </div>
    );
  } else if (context.action === "upload") {
    content = <ActionLinkUpload token={token} context={context} onExpired={expire} />;
  } else {
    content = (
      <ActionLinkSign
        token={token}
        context={context}
        onExpired={expire}
        onFinished={forgetActionToken}
      />
    );
  }

  return <PortalShell session={null}>{content}</PortalShell>;
}
